import {
  BuffetEpgItem,
  EpgItem,
  Episode,
  Movie,
  MyChannel,
  TvShow
} from "@/services/apiService/api.types";
import dayjs from "dayjs";
import { DailyEpg } from "@/components/overlay/types";
import AdvancedFormat from "dayjs/plugin/advancedFormat";
import { i18n } from "@/plugins/i18n/vue-i18n-plugin";
import {
  Locale,
  LocaleCategoriesEnum,
  LocaleDatesFilter
} from "@/plugins/i18n/types";
import { Favorite, vuei18nDayjsLocaleMap } from "@/helpers/types";
import { Categories } from "@/helpers/constants";
require("dayjs/locale/sr");
require("dayjs/locale/en");
require("dayjs/locale/mk");

export function getBreakpoint(): MediaQuery {
  let breakpoint = MediaQuery.sm;
  Object.entries(MediaQuery).forEach((value) => {
    if (window.matchMedia(value[1]).matches) {
      // Could you return/break here? Do you need to execute the forEach for each breakpoint after you have found a match?
      breakpoint = value[1];
    }
  });
  return breakpoint;
}

export enum MediaQuery {
  sm = "(min-width: 576px)",
  md = "(min-width: 768px)",
  lg = "(min-width: 992px)",
  xl = "(min-width: 1200px)",
  xxl = "(min-width: 1440px)"
}

export function debounce(
  // eslint-disable-next-line @typescript-eslint/ban-types
  func: Function,
  timeout: number
  // eslint-disable-next-line
): (...args: any) => void {
  let inDebounce: number;
  // eslint-disable-next-line
  return (...args: any) => {
    clearTimeout(inDebounce);
    inDebounce = setTimeout(() => {
      func(...args);
    }, timeout);
  };
}

export function digitCount(num: number): number {
  if (num === 0) return 1;
  return Math.floor(Math.log10(Math.abs(num))) + 1;
}

export function formatEpgDates<T extends EpgItem | BuffetEpgItem>(
  epgList: T[],
  offset: number
): T[] {
  for (let i = epgList.length - 1; i >= 0; i--) {
    if (epgList[i].epgStarttime !== null) {
      epgList[i].epgStarttime = dayjs(epgList[i].epgStarttime)
        .add(offset, "hours")
        .format("YYYY-MM-DD HH:mm:ss");
      epgList[i].epgEndtime = dayjs(epgList[i].epgEndtime)
        .add(offset, "hours")
        .format("YYYY-MM-DD HH:mm:ss");
    } else {
      epgList[i].starttime = dayjs(epgList[i].starttime)
        .add(offset, "hours")
        .format("YYYY-MM-DD HH:mm:ss");
      epgList[i].endtime = dayjs(epgList[i].endtime)
        .add(offset, "hours")
        .format("YYYY-MM-DD HH:mm:ss");
    }
  }

  return epgList;
}

export function getEpgDates(): DailyEpg[] {
  const dayJsLocale = getDayJsLocale();
  const epgDates: DailyEpg[] = [];
  dayjs.extend(AdvancedFormat);
  for (let i = -6; i < 4; i++) {
    switch (i) {
      case -1:
        epgDates.push({
          name: i18n.t(LocaleDatesFilter.yesterday) as string,
          date: dayjs().subtract(Math.abs(i), "days").format("YYYY-MM-DD")
        });
        break;
      case 0:
        epgDates.push({
          name: i18n.t(LocaleDatesFilter.today) as string,
          date: dayjs().locale("sr").format("YYYY-MM-DD")
        });
        break;
      case 1:
        epgDates.push({
          name: i18n.t(LocaleDatesFilter.tomorrow) as string,
          date: dayjs().add(Math.abs(i), "days").format("YYYY-MM-DD")
        });
        break;
      default:
        if (i < 0) {
          epgDates.push({
            name: dayjs()
              .locale(dayJsLocale)
              .subtract(Math.abs(i), "days")
              .format("Do MMM"),
            date: dayjs()
              .locale(dayJsLocale)
              .subtract(Math.abs(i), "days")
              .format("YYYY-MM-DD")
          });
        }

        if (i > 0) {
          epgDates.push({
            name: dayjs()
              .locale(dayJsLocale)
              .add(Math.abs(i), "days")
              .format("Do MMM"),
            date: dayjs()
              .locale(dayJsLocale)
              .add(Math.abs(i), "days")
              .format("YYYY-MM-DD")
          });
        }
        break;
    }
  }

  return epgDates;
}

export function getDayJsLocale(): string {
  const dayJsLocale =
    vuei18nDayjsLocaleMap[i18n.locale as keyof typeof vuei18nDayjsLocaleMap];
  return dayJsLocale ? dayJsLocale : Locale.EN;
}

export function scrollToPage(
  elemId: string,
  position: ScrollLogicalPosition = "center"
): void {
  document.getElementById(elemId)?.scrollIntoView({
    behavior: "smooth",
    block: position
  });
}

const categoriesType = Categories;

//translated categories that did not come from the api(all channels, favorites)
export function getTranslatedCategory(category: Categories): string {
  let formattedCategory =
    categoriesType["Foreign informative"] === category
      ? "Foreign Info"
      : category;

  switch (formattedCategory) {
    case Categories.AllChannels:
      formattedCategory = i18n.t(LocaleCategoriesEnum.allChannels) as string;
      break;
    case Categories.Favorites:
      formattedCategory = i18n.t(LocaleCategoriesEnum.favorites) as string;
      break;
  }

  return formattedCategory as string;
}

export function filterFavorites<T extends Favorite>(content: T[]): T[] {
  return content.filter((elem) => {
    return elem.isFavorite;
  });
}

export function arrowKeysPreventDefault(event: KeyboardEvent): void {
  if (
    ["Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].indexOf(
      event.code
    ) > -1
  ) {
    event.preventDefault();
  }
}

export function isMovie(
  x: Movie | TvShow | Episode | BuffetEpgItem | MyChannel | null
): x is Movie {
  return x !== null && (x as Movie).file_name !== undefined;
}

export function isTvShow(
  x: Movie | TvShow | Episode | BuffetEpgItem | MyChannel | null
): x is TvShow {
  return (
    x !== null &&
    !("file_name" in (x as TvShow)) &&
    !("videoLive" in (x as TvShow)) &&
    !("catchup_tv_url" in (x as TvShow))
  );
}

export function isEpisode(
  x: Movie | TvShow | Episode | EpgItem | null
): x is Episode {
  return x !== null && (x as Episode).season_id !== undefined;
}

export function ensureHttps(url: string): string{
  if (!url) return "";
  return url.startsWith("https://")
    ? url
    : url.replace(/^http:\/\//, "https://");
}
